import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft, Plus, Trash2, FileText, Users as UsersIcon, Mic, ImageIcon,
  Music, Palette, Film, Layers, Monitor, Box, Eye, Zap, Clock,
  User, ChevronRight, Link2, Unlink, Clapperboard
} from 'lucide-react';

const ICON_MAP = {
  'file-text': FileText, 'users': UsersIcon, 'image': ImageIcon, 'mic': Mic,
  'music': Music, 'palette': Palette, 'film': Film, 'layers': Layers,
  'monitor': Monitor, 'box': Box, 'eye': Eye, 'zap': Zap, 'layout': Layers, 'file': FileText,
};

export default function ChapterDetailPage({ chapterId, projectId, chapterName, onBack }) {
  const { api } = useAuth();
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [masterItems, setMasterItems] = useState([]);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkCategory, setLinkCategory] = useState('');
  const [linkSearch, setLinkSearch] = useState('');

  const loadData = useCallback(async () => {
    const [s, c] = await Promise.all([
      api.get(`/chapters/${chapterId}/summary`),
      api.get(`/projects/${projectId}/categories`),
    ]);
    setSummary(s.data);
    setCategories(c.data);
  }, [api, chapterId, projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  const openLinkModal = async (catKey) => {
    setLinkCategory(catKey);
    const res = await api.get(`/projects/${projectId}/master-library?category=${catKey}`);
    setMasterItems(res.data);
    setShowLinkModal(true);
  };

  const linkItem = async (masterItemId) => {
    await api.post(`/chapters/${chapterId}/link`, { master_item_id: masterItemId, project_id: projectId });
    setShowLinkModal(false);
    loadData();
  };

  const unlinkItem = async (refId) => {
    await api.delete(`/chapters/${chapterId}/link/${refId}`);
    loadData();
  };

  if (!summary) return <div className="flex items-center justify-center h-64"><div className="animate-pulse-slow text-zinc-600 text-sm">Loading chapter...</div></div>;

  const linkedItems = summary.linked_items || {};
  const linkedTotal = summary.total_linked || 0;

  // Get already linked item IDs for filtering
  const linkedIds = new Set();
  Object.values(linkedItems).forEach(cat => cat.items?.forEach(i => linkedIds.add(i.id)));
  const availableToLink = masterItems.filter(i => !linkedIds.has(i.id) && (!linkSearch || i.name.toLowerCase().includes(linkSearch.toLowerCase())));

  return (
    <div className="space-y-4" data-testid="chapter-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button data-testid="back-to-project" onClick={onBack} className="h-8 w-8 bg-zinc-800 hover:bg-zinc-700 rounded-sm flex items-center justify-center">
          <ArrowLeft className="w-4 h-4 text-zinc-400" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-heading font-bold text-white">{chapterName || summary.chapter?.name}</h1>
          <p className="text-[10px] text-zinc-500">
            {summary.chapter?.duration_minutes ? `${summary.chapter.duration_minutes} min | ` : ''}
            {linkedTotal} linked items from master library | {summary.total_shots} shots
          </p>
        </div>
        <span className={`status-${summary.chapter?.status || 'not_started'} px-2 py-1 rounded-full text-[10px] font-medium`}>{summary.chapter?.status?.replace('_', ' ')}</span>
      </div>

      {/* Category Cards — shows what's linked from master */}
      <div className="space-y-3">
        {categories.filter(cat => {
          const catData = linkedItems[cat.key];
          return catData?.total > 0 || ['character', 'background', 'prop', 'voice_over', 'screenplay', 'concept_art', 'storyboard', 'animatic', 'sound_design'].includes(cat.key);
        }).map(cat => {
          const Icon = ICON_MAP[cat.icon] || FileText;
          const catData = linkedItems[cat.key] || { total: 0, items: [] };
          return (
            <div key={cat.key} className="bg-zinc-900 border border-zinc-800 rounded-sm" data-testid={`chapter-cat-${cat.key}`}>
              <div className="px-4 py-2.5 border-b border-zinc-800/50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className="w-3.5 h-3.5" style={{ color: cat.color }} />
                  <span className="text-xs font-semibold text-zinc-300">{cat.label}</span>
                  <span className="text-[10px] font-mono text-zinc-600">{catData.total}</span>
                </div>
                <button data-testid={`link-from-${cat.key}`} onClick={() => openLinkModal(cat.key)}
                  className="h-6 px-2.5 bg-blue-600/20 border border-blue-500/30 text-blue-400 text-[10px] font-medium rounded-sm hover:bg-blue-600/30 flex items-center gap-1">
                  <Link2 className="w-3 h-3" /> Link from Library
                </button>
              </div>
              {catData.items?.length > 0 ? (
                <div className="divide-y divide-zinc-800/20">
                  {catData.items.map(item => (
                    <div key={item.id} className="px-4 py-2 flex items-center justify-between hover:bg-zinc-800/20 transition-colors" data-testid={`linked-item-${item.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-zinc-200">{item.name}</p>
                          <span className={`status-${item.status} px-1 py-0.5 rounded-full text-[8px]`}>{item.status?.replace('_',' ')}</span>
                        </div>
                        {item.description && <p className="text-[10px] text-zinc-600 truncate mt-0.5">{item.description}</p>}
                        <div className="flex items-center gap-2 mt-0.5">
                          {item.assignee_name && <span className="text-[9px] text-zinc-500 flex items-center gap-0.5"><User className="w-2.5 h-2.5" />{item.assignee_name}</span>}
                        </div>
                      </div>
                      <button onClick={() => unlinkItem(item.ref_id)} className="h-5 px-1.5 text-zinc-700 hover:text-red-400 transition-colors" title="Unlink from chapter">
                        <Unlink className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-4 py-4 text-center">
                  <p className="text-[10px] text-zinc-700">No {cat.label.toLowerCase()} linked to this chapter</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Shots */}
      {summary.shots?.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
          <div className="px-4 py-2.5 border-b border-zinc-800">
            <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Shots ({summary.shots.length})</h3>
          </div>
          <table className="w-full">
            <thead><tr className="border-b border-zinc-800/50 text-[10px] text-zinc-500 uppercase">
              <th className="text-left px-4 py-2 font-medium">Shot</th>
              <th className="text-left px-4 py-2 font-medium">Complexity</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
            </tr></thead>
            <tbody className="divide-y divide-zinc-800/30">
              {summary.shots.map(s => (
                <tr key={s.id} className="hover:bg-zinc-800/20">
                  <td className="px-4 py-2 text-xs font-mono text-zinc-300">{s.name}</td>
                  <td className="px-4 py-2"><div className="flex gap-0.5">{Array.from({length:5}).map((_,i) => <div key={i} className={`w-1.5 h-1.5 rounded-sm ${i < (s.complexity||3) ? 'bg-amber-500' : 'bg-zinc-800'}`} />)}</div></td>
                  <td className="px-4 py-2"><span className={`status-${s.status} px-1.5 py-0.5 rounded-full text-[9px]`}>{s.status?.replace('_',' ')}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Link from Library Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="link-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-[32rem]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-heading font-semibold text-white">Link from Master Library</h3>
              <button onClick={() => setShowLinkModal(false)} className="text-zinc-600 hover:text-zinc-300"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="relative mb-3">
              <input value={linkSearch} onChange={e => setLinkSearch(e.target.value)} placeholder="Search master library..." className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div className="max-h-60 overflow-y-auto space-y-1">
              {availableToLink.map(item => (
                <button key={item.id} onClick={() => linkItem(item.id)} data-testid={`link-item-${item.id}`}
                  className="w-full text-left px-3 py-2 bg-zinc-800/30 border border-zinc-800/50 rounded-sm hover:border-blue-500/50 hover:bg-blue-500/10 transition-all">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-zinc-200">{item.name}</p>
                      {item.description && <p className="text-[10px] text-zinc-600 truncate">{item.description}</p>}
                    </div>
                    <Link2 className="w-3.5 h-3.5 text-blue-400" />
                  </div>
                </button>
              ))}
              {availableToLink.length === 0 && (
                <p className="text-xs text-zinc-600 text-center py-4">
                  {masterItems.length === 0 ? 'No items in master library for this category. Add items to the Master Library first.' : 'All items already linked or no matches.'}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
